/**
 * WP-V2-CS — Kawiarnia „Kość": pokój interaktywny (PLAN-V2 §6 Cafe).
 * Konsumuje: AnimatedDogAvatar, ObjectLayer, WorldCamera, WalkController.
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { worldBackButton } from '../../core/worldBackButton.js';
import {
  AnimatedDogAvatar,
  OutfitStore,
  preloadAnchors,
} from '../../avatar/index.js';
import {
  ObjectLayer,
  playFx,
  type InteractEvent,
  type InteractTarget,
} from '../../interact/index.js';
import {
  setupWorld,
  follow,
  WalkController,
} from '../../core/world/index.js';
import {
  CAFE_COLLIDERS,
  CAFE_OBJECT_DEFS,
  CAFE_PLACEMENTS,
  CAFE_SNAP_ZONES,
  CAFE_WORLD_H,
  CAFE_WORLD_W,
} from './objects.js';
import { ensureCafeTextures } from './cafeVisuals.js';
import {
  INTERACT_BUS_EVENT,
  cafeMissionFromInteract,
} from './missionHook.js';

/** Klucz tekstury pieska-kelnera (dog_*_idle), z fallbackiem. */
function waiterDogTextureKey(scene: Phaser.Scene, breed: string): string | null {
  const primary = `dog_${breed}_idle`;
  if (scene.textures.exists(primary)) return primary;
  if (scene.textures.exists('dog_maltese_idle')) return 'dog_maltese_idle';
  if (scene.textures.exists('ph_dog')) return 'ph_dog';
  return null;
}

/** Proceduralna „kość" na czapce kelnera (bez emoji). */
function makeBoneHat(scene: Phaser.Scene): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics();
  g.fillStyle(0xffe0b2, 1);
  g.fillEllipse(0, 0, 28, 12);
  g.fillCircle(-12, -4, 7);
  g.fillCircle(12, -4, 7);
  g.fillCircle(-12, 4, 7);
  g.fillCircle(12, 4, 7);
  return g;
}

/** Prosty NPC kelner — InteractTarget (give → serduszka + „dziękuję"). */
class WaiterNpc implements InteractTarget {
  x: number;
  y: number;
  isMoving = false;
  readonly body: Phaser.GameObjects.Container;
  private readonly scene: Phaser.Scene;
  private thankLabel: Phaser.GameObjects.Text | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number, breed = 'maltese') {
    this.scene = scene;
    this.x = x;
    this.y = y;
    const body = scene.add.circle(0, 0, 34, 0xfff8f0).setStrokeStyle(3, 0xe8d5c4);
    const dogKey = waiterDogTextureKey(scene, breed);
    const face = dogKey
      ? scene.add.image(0, -2, dogKey).setDisplaySize(56, 56)
      : scene.add.circle(0, -2, 22, 0xffe0b2).setStrokeStyle(2, 0xffcc80);
    const hat = makeBoneHat(scene);
    hat.setPosition(0, -38);
    const tag = scene.add
      .text(0, 48, 'Kelner', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffcc',
        padding: { x: 6, y: 2 },
      })
      .setOrigin(0.5, 0);
    this.body = scene.add.container(x, y, [body, face, hat, tag]);
    this.body.setDepth(y + 2);
    this.body.setSize(72, 72);
    this.body.setInteractive(
      new Phaser.Geom.Rectangle(-40, -40, 80, 80),
      Phaser.Geom.Rectangle.Contains,
    );
    this.body.on('pointerdown', () => {
      // kelner był „martwy" na klik — teraz powitanie + FX (give nadal przez ObjectLayer)
      playFx(this.scene, 'hearts', this.x, this.y - 40);
      void this.playAction('happy');
      this.showBubble('Cześć! Weź ciastko! ☕');
    });
    this.body.setName('hotspot-cafe-waiter');
  }

  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  moveTo(_x: number, _y: number): void {
    /* statyczny kelner */
  }

  holdItem(_textureKey: string | null): void {
    /* NPC nie trzyma w pyszczku gracza */
  }

  async playAction(a: string): Promise<void> {
    const scale = a === 'eat' || a === 'happy' ? 1.18 : 1.08;
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this.body,
        scaleX: scale,
        scaleY: scale,
        duration: 180,
        yoyo: true,
        onComplete: () => resolve(),
      });
    });
  }

  /** Ikona „dziękuję" + serduszka (PLAN: podaj NPC). */
  showThanks(): void {
    playFx(this.scene, 'hearts', this.x, this.y - 40);
    this.showBubble('Dziękuję! 💕');
    void this.playAction('happy');
  }

  showBubble(text: string): void {
    this.thankLabel?.destroy();
    this.thankLabel = this.scene.add
      .text(this.x, this.y - 70, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#c2185b',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(8000);
    this.scene.tweens.add({
      targets: this.thankLabel,
      y: this.y - 110,
      alpha: 0,
      duration: 1200,
      onComplete: () => {
        this.thankLabel?.destroy();
        this.thankLabel = null;
      },
    });
  }

  destroy(): void {
    this.thankLabel?.destroy();
    this.body.destroy(true);
  }
}

export class CafeScene extends BaseScene {
  static readonly KEY = 'Cafe';

  private avatar: AnimatedDogAvatar | null = null;
  private walk: WalkController | null = null;
  private layer: ObjectLayer | null = null;
  private waiter: WaiterNpc | null = null;
  private unsubLayer: (() => void) | null = null;
  private steamNodes: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super(CafeScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_cafe');
    // piesek — maltese idle + walk (fallback proceduralny w avatarze)
    for (const pose of ['idle', 'walkA', 'walkB', 'sit', 'jump', 'wave'] as const) {
      const key = `dog_maltese_${pose}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/maltese_${pose}.png`);
      }
    }
    if (!this.textures.exists('ph_dog')) {
      this.load.image('ph_dog', '/assets/placeholder/dog.png');
    }
    void preloadAnchors(this);
  }

  create(): void {
    setupWorld(this, { worldW: CAFE_WORLD_W, worldH: CAFE_WORLD_H, bg: 0xffe4c4 });
    // PNG z preload; gdy brak — proceduralne kształty (bez emoji-fallbacku)
    try {
      ensureCafeTextures(this);
    } catch {
      /* headless */
    }
    this.drawInterior();
    this.spawnPlayer();
    this.spawnWaiterAndLayer();
    this.spawnCookingHotspot();
    this.spawnBackButton();
    this.bindSpecialFx();

    this.events.once('shutdown', () => this.teardown());
    this.registry.set('companionFollow', { x: CAFE_WORLD_W / 2, y: 420 });
  }

  update(_t: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000);
    this.walk?.update(dt);
    // WalkController jest autorytatywny dla pozycji — snap po animacji awatara
    const pos = this.walk?.position;
    this.avatar?.update(dt);
    if (pos && this.avatar) {
      this.avatar.container.setPosition(pos.x, pos.y);
    }
    this.layer?.update(dt);
  }

  private drawInterior(): void {
    // tło: realne skalowane do świata 1600×720
    if (this.textures.exists('bg_cafe')) {
      this.add
        .image(CAFE_WORLD_W / 2, CAFE_WORLD_H / 2, 'bg_cafe')
        .setDisplaySize(CAFE_WORLD_W, CAFE_WORLD_H)
        .setDepth(-1000);
    }
    // lada (dekor — kolizja w CAFE_COLLIDERS)
    this.add
      .rectangle(640, 360, 760, 70, 0xc68642)
      .setStrokeStyle(3, 0xffffff)
      .setDepth(5);
    // stolik (snap)
    this.add
      .ellipse(500, 500, 140, 50, 0xdeb887)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(4);
    this.add
      .text(CAFE_WORLD_W / 2, 40, 'Kawiarnia „Kość"', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '34px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);
    this.add
      .text(CAFE_WORLD_W / 2, 78, 'Weź ciastko — zjedz albo podaj kelnerowi', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);
  }

  private spawnPlayer(): void {
    const cache = OutfitStore.load();
    const breed =
      cache.breed || (this.registry.get('dogBreed') as string) || 'maltese';
    const color =
      cache.color || (this.registry.get('dogColor') as string) || undefined;
    const startX = 200;
    const startY = 480;
    this.avatar = new AnimatedDogAvatar({
      scene: this,
      x: startX,
      y: startY,
      breed,
      color,
      slots: cache.slots,
      swimSlots: cache.swimSlots,
      depth: 100,
    });

    this.walk = new WalkController(this, this.avatar, {
      colliders: CAFE_COLLIDERS,
      bounds: {
        minX: 48,
        minY: 140,
        maxX: CAFE_WORLD_W - 48,
        maxY: CAFE_WORLD_H - 48,
      },
      enableInput: true,
    });

    follow(this, this.avatar.container, {
      lerp: 0.12,
      deadzone: { width: 100, height: 70 },
    });
  }

  private spawnWaiterAndLayer(): void {
    const breed = (this.registry.get('dogBreed') as string) || 'maltese';
    this.waiter = new WaiterNpc(this, 1180, 340, breed);
    const self = this;
    const playerTarget: InteractTarget = {
      moveTo: (x, y) => {
        self.walk?.moveTo(x, y);
      },
      holdItem: (tex) => {
        self.avatar?.holdItem(tex);
      },
      playAction: (a) => {
        if (!self.avatar) return Promise.resolve();
        if (a === 'sniff') return self.avatar.playAction('happy');
        const known = a as 'sit' | 'jump' | 'wave' | 'eat' | 'swim' | 'sleep' | 'happy';
        return self.avatar.playAction(known);
      },
      getPosition: () =>
        self.walk?.position ?? { x: self.avatar?.x ?? 0, y: self.avatar?.y ?? 0 },
      get isMoving() {
        return self.walk?.isMoving ?? false;
      },
      get x() {
        return self.walk?.position.x ?? self.avatar?.x ?? 0;
      },
      get y() {
        return self.walk?.position.y ?? self.avatar?.y ?? 0;
      },
    };

    this.layer = new ObjectLayer({
      scene: this,
      defs: CAFE_OBJECT_DEFS,
      target: playerTarget,
      giveTargets: [this.waiter],
      interactRange: 100,
      groundBounds: {
        minX: 48,
        maxX: CAFE_WORLD_W - 48,
        minY: 380,
        maxY: CAFE_WORLD_H - 48,
      },
    });
    this.layer.setSnapZones(CAFE_SNAP_ZONES);
    this.layer.spawn(CAFE_PLACEMENTS);

    this.unsubLayer = this.layer.on((evt) => this.onInteract(evt));
  }

  private onInteract(evt: InteractEvent): void {
    // szyna dla WP-V2-QC (misje) + lokalne sceny
    this.game.events.emit(INTERACT_BUS_EVENT, {
      sceneKey: CafeScene.KEY,
      ...evt,
    });
    this.events.emit('interact', evt);

    const mission = cafeMissionFromInteract(evt);
    if (mission) {
      this.game.events.emit(INTERACT_BUS_EVENT, mission);
      this.events.emit('quest-progress', mission);
      this.waiter?.showThanks();
    }

    // specjalne FX poza domyślnym ObjectLayer
    if (evt.type === 'tap' || evt.type === 'toggle') {
      if (evt.defId === 'cafe_espresso') this.playEspressoFx(evt.x, evt.y);
      if (evt.defId === 'cafe_pitcher') this.playPourFx(evt.x, evt.y);
      if (evt.defId === 'cafe_bell') this.playDing(evt.x, evt.y);
      if (evt.defId === 'cafe_chair_a' || evt.defId === 'cafe_chair_b') {
        void this.avatar?.playAction('sit');
        playFx(this, 'hearts', evt.x, evt.y - 20);
      }
      if (evt.defId === 'cafe_menu') this.playMenuPeek(evt.x, evt.y);
      if (evt.defId === 'cafe_tray') {
        playFx(this, 'sparkle', evt.x, evt.y);
        void this.avatar?.playAction('happy');
      }
      if (evt.defId === 'cafe_plant') {
        playFx(this, 'stars', evt.x, evt.y - 10);
        void this.avatar?.playAction('happy');
      }
    }
  }

  private playMenuPeek(x: number, y: number): void {
    playFx(this, 'stars', x, y);
    const t = this.add
      .text(x, y - 40, 'Menu dnia: ciastka! 🧁', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(7000);
    this.tweens.add({
      targets: t,
      y: y - 80,
      alpha: 0,
      duration: 900,
      onComplete: () => t.destroy(),
    });
    void this.avatar?.playAction('happy');
  }

  private bindSpecialFx(): void {
    /* FX wołane z onInteract */
  }

  private playEspressoFx(x: number, y: number): void {
    // kapie kawa
    for (let i = 0; i < 5; i++) {
      const drop = this.add
        .circle(x + 8, y + 10, 4, 0x4e342e, 0.9)
        .setDepth(6000);
      this.tweens.add({
        targets: drop,
        y: y + 50 + i * 4,
        alpha: 0,
        delay: i * 80,
        duration: 400,
        onComplete: () => drop.destroy(),
      });
    }
    // para
    for (let i = 0; i < 4; i++) {
      const steam = this.add
        .circle(x - 6 + i * 6, y - 20, 10 + i * 2, 0xffffff, 0.35)
        .setDepth(6000);
      this.steamNodes.push(steam);
      this.tweens.add({
        targets: steam,
        y: y - 70 - i * 8,
        alpha: 0,
        scaleX: 1.6,
        scaleY: 1.6,
        delay: i * 100,
        duration: 900,
        onComplete: () => {
          steam.destroy();
          this.steamNodes = this.steamNodes.filter((n) => n !== steam);
        },
      });
    }
    playFx(this, 'sparkle', x, y - 30);
  }

  private playPourFx(x: number, y: number): void {
    const stream = this.add
      .rectangle(x + 18, y + 10, 8, 24, 0x90caf9, 0.85)
      .setDepth(6000);
    this.tweens.add({
      targets: stream,
      scaleY: 1.4,
      alpha: 0,
      duration: 500,
      onComplete: () => stream.destroy(),
    });
    playFx(this, 'splash', x + 20, y + 30);
  }

  private playDing(x: number, y: number): void {
    playFx(this, 'notes', x, y);
    const ding = this.add
      .text(x, y - 40, 'ding!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#6a1b9a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(7000);
    this.tweens.add({
      targets: ding,
      y: y - 80,
      alpha: 0,
      duration: 700,
      onComplete: () => ding.destroy(),
    });
  }

  private spawnCookingHotspot(): void {
    const btn = UiKit.button(this, {
      x: 200,
      y: CAFE_WORLD_H - 64,
      label: '👩‍🍳 Gotuj z nami!',
      width: 260,
      height: Math.max(72, MIN_TOUCH),
      fill: 0xffb6c1,
      onClick: () => getSceneRouter().go('MgCooking'),
    });
    btn.setScrollFactor(0);
    btn.setDepth(20_100);
    btn.setData('navTarget', 'MgCooking');
    btn.setName('hotspot-MgCooking');
  }

  private spawnBackButton(): void {
    worldBackButton(this);
  }

  private teardown(): void {
    this.unsubLayer?.();
    this.unsubLayer = null;
    this.layer?.destroy();
    this.layer = null;
    this.walk?.destroy();
    this.walk = null;
    this.avatar?.destroy();
    this.avatar = null;
    this.waiter?.destroy();
    this.waiter = null;
    for (const n of this.steamNodes) n.destroy();
    this.steamNodes = [];
    this.registry.remove('companionFollow');
  }
}

export default CafeScene;
